import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Icon from './Icon';
import { Avatar, Logo } from './UI';
import { useApp } from '../context/AppContext';

const MEMBER_LINKS = [
  { to: '/home', label: 'Lounge', icon: 'home' },
  { to: '/matches', label: 'Matches', icon: 'heart' },
  { to: '/search', label: 'Search', icon: 'search' },
  { to: '/interests', label: 'Inbox', icon: 'inbox', count: 'interests' },
  { to: '/messages', label: 'Chats', icon: 'chat', count: 'chats' },
];

function useScrolled(offset = 12) {
  const [s, setS] = useState(false);
  useEffect(() => {
    const on = () => setS(window.scrollY > offset);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, [offset]);
  return s;
}

function Badge({ n }) {
  return (
    <AnimatePresence>
      {n > 0 && (
        <motion.span className="badge" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} key={n}>
          {n > 9 ? '9+' : n}
        </motion.span>
      )}
    </AnimatePresence>
  );
}

function UserMenu() {
  const { user, logout, isPremium } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const items = [
    { to: `/profile/${user.profileId}`, icon: 'user', label: 'View my profile' },
    { to: '/me/edit', icon: 'edit', label: 'Edit profile' },
    { to: '/me/preferences', icon: 'sliders', label: 'Partner preferences' },
    { to: '/shortlist', icon: 'star', label: 'Shortlisted' },
    { to: '/visitors', icon: 'eye', label: 'Who viewed me' },
    { to: '/settings', icon: 'settings', label: 'Privacy & settings' },
    { to: '/help', icon: 'help', label: 'Help & safety' },
  ];
  return (
    <div className="umenu" ref={ref}>
      <button className="umenu__btn" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Avatar src={user.photos?.[0]} name={user.name} size={38} />
        {isPremium && <span className="umenu__crown"><Icon name="crown" size={12} stroke={2.2} /></span>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="umenu__panel"
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18 }}
          >
            <div className="umenu__head">
              <Avatar src={user.photos?.[0]} name={user.name} size={46} />
              <div>
                <strong>{user.name}</strong>
                <small className="muted">{user.profileId} · {isPremium ? 'Premium' : 'Free member'}</small>
              </div>
            </div>
            {items.map((i) => (
              <Link key={i.to} to={i.to} className="umenu__item">
                <Icon name={i.icon} size={17} /> {i.label}
              </Link>
            ))}
            <button
              className="umenu__item umenu__item--danger"
              onClick={() => {
                // Full reload: clears all in-memory state and avoids route guards
                // racing the exit animation of the current page.
                logout();
                window.location.assign('/');
              }}
            >
              <Icon name="logout" size={17} /> Logout
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Navbar() {
  const { user, counts, isPremium } = useApp();
  const scrolled = useScrolled();
  const { pathname } = useLocation();
  const onLanding = pathname === '/';
  return (
    <motion.header
      className={`nav ${scrolled || !onLanding ? 'nav--solid' : ''}`}
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
    >
      <div className="nav__inner container">
        <Logo to={user ? '/home' : '/'} />
        {user ? (
          <>
            <nav className="nav__links">
              {MEMBER_LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className="nav__link">
                  {({ isActive }) => (
                    <>
                      {isActive && <motion.span layoutId="nav-active" className="nav__active" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />}
                      <span className="nav__label">
                        <Icon name={l.icon} size={17} />
                        {l.label}
                        {l.count && <Badge n={counts[l.count]} />}
                      </span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="nav__right">
              {!isPremium && (
                <Link to="/premium" className="btn btn--gold btn--sm shine">
                  <Icon name="crown" size={16} /> Upgrade
                </Link>
              )}
              <Link to="/notifications" className="icon-btn icon-btn--bell" aria-label="Notifications">
                <Icon name="bell" />
                <Badge n={counts.notifications} />
              </Link>
              <UserMenu />
            </div>
          </>
        ) : (
          <div className="nav__right">
            <nav className="nav__public">
              <Link to="/stories">Success stories</Link>
              <Link to="/premium">Membership</Link>
              <Link to="/help">Help</Link>
            </nav>
            <Link to="/login" className="btn btn--ghost btn--sm">Login</Link>
            <Link to="/register" className="btn btn--primary btn--sm">Register free</Link>
          </div>
        )}
      </div>
    </motion.header>
  );
}

export function MobileDock() {
  const { user, counts } = useApp();
  if (!user) return null;
  return (
    <nav className="dock">
      {MEMBER_LINKS.map((l) => (
        <NavLink key={l.to} to={l.to} className="dock__item">
          {({ isActive }) => (
            <>
              {isActive && <motion.span layoutId="dock-active" className="dock__active" />}
              <span className="dock__icon">
                <Icon name={l.icon} size={21} />
                {l.count && <Badge n={counts[l.count]} />}
              </span>
              <small>{l.label}</small>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="container footer__grid">
        <div>
          <Logo />
          <p className="muted footer__tag">The matrimonial home for doctors, nurses and every healthcare hero. Built with love, verified with care.</p>
          <div className="footer__medhire">
            <Icon name="steth" size={18} /> Official matrimony partner of <strong>MedHire</strong>
          </div>
        </div>
        <div>
          <h4>Explore</h4>
          <Link to="/register">Register free</Link>
          <Link to="/search">Search profiles</Link>
          <Link to="/stories">Success stories</Link>
          <Link to="/premium">Membership plans</Link>
        </div>
        <div>
          <h4>Support</h4>
          <Link to="/help">Help centre</Link>
          <Link to="/help#safety">Safety tips</Link>
          <Link to="/help#privacy">Privacy policy</Link>
          <Link to="/help#terms">Terms of use</Link>
        </div>
        <div>
          <h4>Reach us</h4>
          <span className="muted">care@ctshaadi.com</span>
          <span className="muted">+91 1800 120 4545</span>
          <span className="muted">Mon–Sat, 9am – 8pm IST</span>
        </div>
      </div>
      <div className="container footer__bottom">
        <span>© {new Date().getFullYear()} CTshaadi.com · Made in India with ♥</span>
        <span className="muted">Profiles are screened, but please follow our safety tips before meeting anyone.</span>
      </div>
    </footer>
  );
}
